import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, Bandage, Users, ChevronDown } from "lucide-react";

const CARDS = [
  {
    id: "envie",
    testid: "crise-envie",
    Icon: Flame,
    tone: "orange",
    title: "Si l'envie est trop forte",
    teaser: "3 gestes pour la laisser passer.",
    steps: [
      "Respire 4·2·4 pendant 60 secondes.",
      "Tiens un glaçon dans la main, fort.",
      "Dessine, cris dans un oreiller, bouge 30 secondes.",
      "Appelle le 3114 si ça reste trop fort.",
    ],
  },
  {
    id: "blessure",
    testid: "crise-blessure",
    Icon: Bandage,
    tone: "emerald",
    title: "Soigner une blessure",
    teaser: "Les bons réflexes, calmement.",
    steps: [
      "Lave avec de l'eau tiède, pas d'alcool pur.",
      "Sèche doucement, applique une compresse propre.",
      "Si ça saigne > 15 min ou profond : appelle le 15.",
      "Tu as le droit de montrer à un médecin, sans jugement.",
    ],
  },
  {
    id: "proche",
    testid: "crise-proche",
    Icon: Users,
    tone: "sky",
    title: "Parler à un proche",
    teaser: "Briser le silence, un mot à la fois.",
    steps: [
      "Choisis une personne de confiance et un moment calme.",
      "Dis simplement : « J'ai besoin de parler, c'est important. »",
      "Pas besoin de tout expliquer. Un mot suffit à ouvrir la porte.",
      "Si c'est trop dur à l'oral : écris un message ou montre ce site.",
    ],
  },
];

const TONES = {
  orange: {
    bg: "rgba(249, 115, 22, 0.12)",
    border: "rgba(249, 115, 22, 0.35)",
    accent: "#F97316",
    text: "text-orange-200",
    iconBg: "bg-orange-500/15 border-orange-500/30",
    iconText: "text-orange-300",
  },
  emerald: {
    bg: "rgba(16, 185, 129, 0.12)",
    border: "rgba(16, 185, 129, 0.35)",
    accent: "#10B981",
    text: "text-emerald-200",
    iconBg: "bg-emerald-500/15 border-emerald-500/30",
    iconText: "text-emerald-300",
  },
  sky: {
    bg: "rgba(56, 189, 248, 0.12)",
    border: "rgba(56, 189, 248, 0.35)",
    accent: "#38BDF8",
    text: "text-sky-200",
    iconBg: "bg-sky-500/15 border-sky-500/30",
    iconText: "text-sky-300",
  },
};

export default function CrisisCards() {
  const [open, setOpen] = useState(null);

  return (
    <section id="crise" className="py-16 lg:py-20 border-t border-white/5 bg-[#0A1020]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <span className="em-eyebrow">En cas de crise</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
          Que faire maintenant, concrètement ?
        </h2>
        <p className="mt-3 text-slate-300 max-w-2xl">
          Clique sur une carte pour voir les gestes essentiels — courts, clairs, pas de blabla.
        </p>

        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {CARDS.map((c) => {
            const t = TONES[c.tone];
            const isOpen = open === c.id;
            return (
              <motion.article
                key={c.id}
                layout
                data-testid={c.testid}
                onClick={() => setOpen(isOpen ? null : c.id)}
                className="cursor-pointer rounded-2xl border p-6 transition hover:-translate-y-0.5"
                style={{
                  background: `linear-gradient(180deg, ${t.bg}, rgba(15, 23, 42, 0.9))`,
                  borderColor: t.border,
                }}
                role="button"
                aria-expanded={isOpen}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`w-11 h-11 rounded-xl ${t.iconBg} border grid place-items-center`}>
                    <c.Icon className={`w-5 h-5 ${t.iconText}`} />
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition ${isOpen ? "rotate-180 text-white" : ""}`}
                  />
                </div>
                <h3 className="mt-4 text-white font-semibold text-lg font-[Outfit]">
                  {c.title}
                </h3>
                <p className={`mt-1 text-sm ${t.text}`}>{c.teaser}</p>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.ol
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 space-y-2 overflow-hidden"
                      data-testid={`${c.testid}-steps`}
                    >
                      {c.steps.map((s, i) => (
                        <li key={i} className="flex gap-3 text-sm text-slate-200">
                          <span
                            className="shrink-0 w-6 h-6 rounded-full grid place-items-center text-xs font-semibold text-white"
                            style={{ background: t.accent }}
                          >
                            {i + 1}
                          </span>
                          <span className="pt-0.5">{s}</span>
                        </li>
                      ))}
                    </motion.ol>
                  )}
                </AnimatePresence>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
